import { useCallback, useEffect } from 'react';
import useResizeObserver from 'use-resize-observer';
import { debounce } from '@/helpers';

interface IProps {
  wait: number;
  /**
   * Make sure to memoize this function to avoid excessive invocations
   */
  onResize?: (size: ISize) => void;
}

interface ISize {
  width: number | undefined;
  height: number | undefined;
}

interface IHookDebouncedResizeObserver {
  ref: (instance: HTMLElement | null) => void;
  cancelDebouncedCallback: () => void;
}

const useDebouncedResizeObserver = (props: IProps): IHookDebouncedResizeObserver => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCallback = useCallback(
    debounce(
      (size: ISize) =>
        requestAnimationFrame(() => {
          props.onResize && props.onResize(size);
        }),
      props.wait,
      { maxWait: 1000 },
    ),
    [props.wait, props.onResize],
  );

  useEffect(() => {
    return () => {
      debouncedCallback.cancel();
    };
  }, [debouncedCallback]);

  const { ref } = useResizeObserver({ onResize: debouncedCallback });

  return {
    ref,
    cancelDebouncedCallback: debouncedCallback.cancel,
  };
};

export { useDebouncedResizeObserver };
